import styled from "@emotion/styled";
import { UserLogList } from "./UserLogList";
import { ChatLogData } from "@shared/apis/sidekick/generated";
import { useGetUser } from "@queries/users/useGetUser";
import { useSearchParams } from "react-router-dom";
import {
  CircularProgress,
  IconButton,
  InputAdornment,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { format } from "date-fns";
import { parsePresentUnixDate } from "@shared/utils/date-parser";
import { TextField } from "@shared/components/TextField";
import Fuse from "fuse.js";
import { useCallback, useMemo, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

const Container = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const UserThreadLogInfoBar = styled.div`
  position: sticky;
  width: 100%;
  top: 0;
  margin-bottom: 1rem;
  padding: 1rem 0;
  background-color: ${(p) => p.theme.colors.background2};
  z-index: 100;
`;

const convertUserLogs = (data: ChatLogData[], userName: string) => {
  return data.map((log) => ({
    id: `${log.action}:${new Date(log.createdAt).getTime()}`,
    data: log.data,
    createdAt: new Date(log.createdAt),
    action: log.action,
    createdBy: log.action === "prompt-sent" ? "user" : "bot",
    userName: log.action === "prompt-sent" ? userName : undefined,
  }));
};

type Props = {
  userId: string;
  threadId: string;
  threadTitle: string;
  threadLogs: ChatLogData[];
  isLoadingThreadLogs: boolean;
};

type LogDataChunkMessage = {
  content?: Array<{
    text: {
      value: string;
    };
    type: "text";
  }>;
};

export function UserThreadLogs(props: Props) {
  const { userId, threadTitle, threadLogs, isLoadingThreadLogs } = props;
  const userQuery = useGetUser({ id: userId });
  const userName = userQuery.data?.name ?? "";
  const [search] = useSearchParams();
  const createdAtStart = parsePresentUnixDate(search.get("created_at_start"));
  const createdAtFinish = parsePresentUnixDate(search.get("created_at_finish"));
  const [searchTerm, setSearchTerm] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const debouncedSearch = useDebouncedCallback((value: string) => {
    setSearchTerm(value);
  }, 400);

  const filteredThreadLogs = useMemo(() => {
    const fuse = new Fuse(threadLogs, {
      keys: [
        { name: "action" },
        { name: "data.message" },
        { name: "data.run.status" },
        {
          name: "data.messages[]",
          getFn: (item: any) => {
            if (item.data.messages && item.data.messages[0]?.content) {
              return item.data.messages
                .map((message: LogDataChunkMessage) =>
                  (message.content ?? [])
                    .map((content) => content.text?.value || "")
                    .join(" ")
                )
                .join(" ");
            }
            return "";
          },
        },
      ],
      threshold: 0.4,
    });

    return searchTerm
      ? fuse.search(searchTerm).map((item) => item.item)
      : threadLogs;
  }, [threadLogs, searchTerm]);

  const handleSearch = useCallback(
    (e: any) => {
      const value = e.target.value;
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const clearSearch = useCallback(() => {
    setSearchTerm("");
    if (searchRef.current) {
      searchRef.current.value = "";
    }
  }, []);

  return (
    <Container>
      <TextField
        name="search"
        placeholder="Search"
        onChange={handleSearch}
        inputRef={searchRef}
        InputProps={{
          endAdornment: (
            <InputAdornment position="start">
              {debouncedSearch.isPending() ? (
                <IconButton size="small" disabled>
                  <CircularProgress size={20} />
                </IconButton>
              ) : (
                <IconButton size="small" onClick={clearSearch}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
      />
      <UserThreadLogInfoBar>
        <Typography variant="h6">{threadTitle}</Typography>
        {createdAtStart && createdAtFinish && (
          <Typography variant="body2">
            Showing prompts from{" "}
            <strong>
              {format(new Date(createdAtStart), "MM/dd/yyyy hh:mm:ss a")}
            </strong>{" "}
            to{" "}
            <strong>
              {format(new Date(createdAtFinish), "MM/dd/yyyy hh:mm:ss a")}
            </strong>
          </Typography>
        )}
      </UserThreadLogInfoBar>
      <UserLogList
        userLogs={convertUserLogs(filteredThreadLogs, userName)}
        isLoadingUserLogs={isLoadingThreadLogs}
        createdAtStart={createdAtStart}
        createdAtFinish={createdAtFinish}
        notFoundMessage={
          !!searchTerm && filteredThreadLogs.length === 0
            ? "No results found."
            : "This thread has no logs."
        }
      />
    </Container>
  );
}
